import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea"]

  declare readonly textareaTarget: HTMLTextAreaElement

  private boundHandleVisibility = this.handleVisibility.bind(this)

  connect() {
    // Initialize with proper height (no animation on first load)
    this.grow(false)

    // Listen for tab visibility changes (when textarea is in a hidden tab)
    // Events bubble up, so we listen on document and check if we're inside the shown panel
    document.addEventListener("tab-panel:shown", this.boundHandleVisibility)
  }

  disconnect() {
    document.removeEventListener("tab-panel:shown", this.boundHandleVisibility)
  }

  private handleVisibility(event: Event) {
    // Only respond if this textarea is inside the panel that was shown
    const panel = event.target as HTMLElement
    if (panel.contains(this.element)) {
      // Re-calculate height when becoming visible (no animation)
      this.grow(false)
    }
  }

  grow(animate = true) {
    const textarea = this.textareaTarget

    // Temporarily disable transition for measurement if not animating
    const originalTransition = textarea.style.transition
    if (!animate) {
      textarea.style.transition = 'none'
    }

    // Reset height to auto to get accurate scrollHeight
    const currentHeight = textarea.offsetHeight
    textarea.style.height = 'auto'

    // Measure scrollHeight for actual content
    let newHeight = textarea.scrollHeight

    // If textarea is empty and has a placeholder, measure placeholder height too
    // Placeholder text doesn't affect scrollHeight, so we need to measure it separately
    if (!textarea.value && textarea.placeholder) {
      newHeight = Math.max(newHeight, this.measurePlaceholderHeight(textarea))
    }

    // Restore current height before animating to new height
    if (animate && Math.abs(currentHeight - newHeight) > 1) {
      textarea.style.height = `${currentHeight}px`
      // Force reflow to ensure transition happens
      textarea.offsetHeight
      textarea.style.height = `${newHeight}px`
    } else {
      textarea.style.height = `${newHeight}px`
    }

    // Restore original transition
    if (!animate) {
      // Force reflow before restoring transition
      textarea.offsetHeight
      textarea.style.transition = originalTransition
    }
  }

  /**
   * Measure the height needed to display placeholder text.
   * Creates a hidden measurement element with the same styling as the textarea.
   */
  private measurePlaceholderHeight(textarea: HTMLTextAreaElement): number {
    // Create a hidden div for measurement
    const measureEl = document.createElement('div')

    // Copy computed styles that affect text layout
    const styles = window.getComputedStyle(textarea)
    measureEl.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: ${textarea.clientWidth}px;
      padding: ${styles.padding};
      border: ${styles.border};
      font-family: ${styles.fontFamily};
      font-size: ${styles.fontSize};
      font-weight: ${styles.fontWeight};
      line-height: ${styles.lineHeight};
      letter-spacing: ${styles.letterSpacing};
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      box-sizing: border-box;
    `

    // Use placeholder text
    measureEl.textContent = textarea.placeholder

    // Append, measure, remove
    document.body.appendChild(measureEl)
    const height = measureEl.scrollHeight
    document.body.removeChild(measureEl)

    return height
  }
}
